import { memoizeFunction, mergeStyles, mergeStyleSets } from '@fluentui/react';
import { isLargeScreen, isMobileScreen, isSmallScreen, ScreenSize } from 'common/utilities';

const baseline = 8;
const size = {
  xxl: baseline * 6,
  xl: baseline * 5,
  lg: baseline * 4,
  md: baseline * 3,
  sm: baseline * 2,
  xs: baseline,
} as const;

const pageTopPadding = size.lg;
const pageHeaderPadding = size.lg;

const contentMaxWidth = 1608;

export const getClassNames = memoizeFunction(
  (
    isVertical: boolean,
    leftPanePerc: number, // memoizing with leftPanePerc and rightPanePadding assuming they do not change frequently
    rightPanePadding: number,
    screenSize: ScreenSize,
    maxWidthOverride?: number,
    paddingOverride?: string
  ) => {
    let pageHorizontalPadding = size.sm;
    if (isMobileScreen(screenSize)) {
      pageHorizontalPadding = size.sm;
    } else if (isSmallScreen(screenSize)) {
      pageHorizontalPadding = size.sm + 65.5;
    } else if (isLargeScreen(screenSize)) {
      pageHorizontalPadding = size.sm + 72;
    }

    return mergeStyleSets(
      {
        pageWrapper: {
          padding: paddingOverride ?? `${pageTopPadding}px ${pageHorizontalPadding}px ${size.xl}px`,
          height: '100%',
          width: '100%',
          maxWidth: maxWidthOverride ?? contentMaxWidth,
          alignSelf: 'center',
        },
      },
      !isVertical && {
        // all these styles are only applicable when the page is rendered in horizontal layout
        pageHeader: {
          display: 'flex',
          flexDirection: 'column',
          gap: size.lg,
          paddingBottom: pageHeaderPadding,
        },
        leftPaneContainer: {
          width: `${leftPanePerc * 100}%`,
        },
        leftPaneSticky: {
          position: 'sticky',
          top: pageTopPadding,
        },
        rightPaneContainer: {
          paddingTop: rightPanePadding,
          width: `${(1 - leftPanePerc) * 100}%`,
        },
      }
    );
  }
);

export const getBackButtonClassName = memoizeFunction((isCondensed: boolean) =>
  mergeStyles(
    isCondensed
      ? {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: 42,
          width: 42,
          alignItems: 'center',
          borderRadius: '50%',
          '&:focus::after': {
            borderRadius: '50%',
          },
        }
      : {
          alignSelf: 'flex-start',
        }
  )
);

export const getPageHeadingClassName = memoizeFunction((isVertical: boolean) =>
  mergeStyles(
    isVertical
      ? {
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gridColumnGap: `${size.sm}px`,
          justifyItems: 'start',
          alignItems: 'center',
          paddingBottom: pageHeaderPadding,
        }
      : {
          display: 'flex',
          flexDirection: 'column',
          gap: size.lg,
          paddingBottom: pageHeaderPadding,
        }
  )
);

export const tokens = {
  twoPaneContainer: {
    childrenGap: 16,
  },
};
